import "./HeroBanner.scss"

import React, { useCallback, useEffect, useRef, useState } from "react"
import { Icon } from "@fider/components"
import { useFider } from "@fider/hooks"
import { classSet } from "@fider/services"
import { PostCategory } from "@fider/services/khcn"
import { announceBrandLanding } from "@fider/services/brandIntro"
import { BronzeDrum } from "./BronzeDrum"
import { HeroLandscape, HeroTech, HeroVariant } from "./HeroScene"
import IconSearch from "@fider/assets/images/heroicons-search.svg"
import IconChevronUp from "@fider/assets/images/chevron-up.svg"
import { i18n } from "@lingui/core"

// Tùy biến KHCN: hero banner trang chủ theo bản thiết kế — slider tự chạy qua các danh lam Hà Tĩnh,
// mỗi slide có lớp phủ công nghệ animation và trống đồng Đông Sơn xoay; tìm kiếm + chip hạng mục ngay trong banner.
//
// ẢNH DANH LAM: thả ảnh vào static/hero/<key>.(jpg|png|webp) — ví dụ static/hero/nga-ba-dong-loc.jpg.
// Slide nào có ảnh sẽ hiện ảnh thật (hiệu ứng zoom chậm); chưa có ảnh thì hiện minh hoạ cách điệu tương ứng.
// Không cần sửa backend: phía client tự thử tải ảnh.

const AUTOPLAY_MS = 6500
// Chỉ dò .jpg và .png (định dạng xuất từ Photoshop) để bớt request thừa khi chưa có ảnh
const EXTENSIONS = ["jpg", "png"]

interface HeroSlide {
  key: string
  name: string
  variant: HeroVariant
}

const slides = (): HeroSlide[] => [
  { key: "thanh-pho-ha-tinh", name: i18n._({ id: "khcn.hero.place.city", message: "Hà Tĩnh City" }), variant: "city" },
  { key: "nga-ba-dong-loc", name: i18n._({ id: "khcn.hero.place.dongloc", message: "Đồng Lộc T-Junction" }), variant: "mountain" },
  { key: "bien-thien-cam", name: i18n._({ id: "khcn.hero.place.thiencam", message: "Thiên Cầm Beach" }), variant: "sea" },
  { key: "chua-huong-tich", name: i18n._({ id: "khcn.hero.place.huongtich", message: "Hương Tích Pagoda" }), variant: "mountain" },
  { key: "deo-ngang", name: i18n._({ id: "khcn.hero.place.ngang", message: "Ngang Pass – Hoành Sơn Gate" }), variant: "mountain" },
  { key: "cang-vung-ang", name: i18n._({ id: "khcn.hero.place.vungang", message: "Vũng Áng Port" }), variant: "sea" },
]

// Thử tải lần lượt các đuôi ảnh; trả về URL đầu tiên tải được, hoặc null
const probeImage = (base: string): Promise<string | null> =>
  EXTENSIONS.reduce<Promise<string | null>>(
    (chain, ext) =>
      chain.then(
        (found) =>
          found ||
          new Promise<string | null>((resolve) => {
            const img = new Image()
            const src = `${base}.${ext}`
            img.onload = () => resolve(src)
            img.onerror = () => resolve(null)
            img.src = src
          })
      ),
    Promise.resolve(null)
  )

interface HeroBannerProps {
  categories: PostCategory[]
  countPerCategory: { [slug: string]: number }
  activeCategory: string | null
  onSelectCategory: (slug: string | null) => void
  onSearch: (query: string) => void
}

export const HeroBanner = (props: HeroBannerProps) => {
  const fider = useFider()
  const items = slides()
  const count = items.length

  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  const [photos, setPhotos] = useState<{ [key: string]: string }>({})
  const [query, setQuery] = useState("")
  // Ấn ký "Hà Tĩnh": idle (đang hiện) → flying (bản sao bay lên header) → done (không hiện nữa cho tới khi tải lại trang)
  const [callig, setCallig] = useState<"idle" | "flying" | "done">("idle")
  const calligRef = useRef<HTMLDivElement>(null)

  const indexRef = useRef(index)
  indexRef.current = index

  const go = useCallback((next: number) => setIndex(((next % count) + count) % count), [count])

  // Tôn trọng người dùng tắt chuyển động: không tự chạy slider, CSS cũng tắt animation
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const apply = () => setReducedMotion(mq.matches)
    apply()
    mq.addEventListener("change", apply)
    return () => mq.removeEventListener("change", apply)
  }, [])

  // Dò ảnh danh lam thật trong static/hero (chỉ chạy ở client)
  useEffect(() => {
    let cancelled = false
    items.forEach((slide) => {
      probeImage(`${fider.settings.assetsURL}/static/assets/hero/${slide.key}`).then((src) => {
        if (src && !cancelled) {
          setPhotos((prev) => ({ ...prev, [slide.key]: src }))
        }
      })
    })
    return () => {
      cancelled = true
    }
  }, [fider.settings.assetsURL])

  // Ấn ký bay vào wordmark trên header (bản sao position: fixed để vượt qua header sticky),
  // gần cuối hành trình thì báo header đổi tên diễn đàn. Không có chuyển động / thẻ đang ẩn => đổi tên ngay.
  const flyToBrand = () => {
    const card = calligRef.current
    const target = document.querySelector<HTMLElement>(".c-header__brand-text") || document.querySelector<HTMLElement>(".c-header__brand")
    if (!card || !target || reducedMotion || getComputedStyle(card).display === "none" || typeof card.animate !== "function") {
      setCallig("done")
      announceBrandLanding()
      return
    }
    const from = card.getBoundingClientRect()
    const to = target.getBoundingClientRect()
    const ghost = card.cloneNode(true) as HTMLElement
    ghost.classList.add("kh-hero__callig--ghost")
    ghost.style.cssText = `position:fixed;left:${from.left}px;top:${from.top}px;width:${from.width}px;height:${from.height}px;margin:0;z-index:200;pointer-events:none;animation:none;`
    document.body.appendChild(ghost)
    setCallig("flying")

    const dx = to.left + to.width / 2 - (from.left + from.width / 2)
    const dy = to.top + to.height / 2 - (from.top + from.height / 2)
    const scale = Math.max(0.1, to.height / from.height)
    const flight = ghost.animate(
      [
        { transform: "translate(0, 0) scale(1) rotate(0deg)", opacity: 1, offset: 0 },
        { transform: `translate(${dx * 0.45}px, ${dy * 0.25}px) scale(0.75) rotate(-5deg)`, opacity: 1, offset: 0.45 },
        { transform: `translate(${dx}px, ${dy}px) scale(${scale}) rotate(0deg)`, opacity: 0.15, offset: 1 },
      ],
      { duration: 1100, easing: "cubic-bezier(0.75, 0, 0.2, 1)", fill: "forwards" }
    )
    window.setTimeout(announceBrandLanding, 820)
    flight.onfinish = () => {
      ghost.remove()
      setCallig("done")
    }
  }

  // Lần cuộn xuống đầu tiên => bay
  useEffect(() => {
    if (callig !== "idle") {
      return
    }
    let fired = false
    const onScroll = () => {
      if (fired || window.scrollY < 40) {
        return
      }
      fired = true
      window.removeEventListener("scroll", onScroll)
      flyToBrand()
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [callig, reducedMotion])

  // Tự chuyển slide
  useEffect(() => {
    if (paused || reducedMotion || count < 2) {
      return
    }
    const timer = window.setInterval(() => go(indexRef.current + 1), AUTOPLAY_MS)
    return () => window.clearInterval(timer)
  }, [paused, reducedMotion, count, go])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      go(index - 1)
    } else if (e.key === "ArrowRight") {
      go(index + 1)
    }
  }

  const touchX = useRef<number | null>(null)
  const onTouchStart = (e: React.TouchEvent) => (touchX.current = e.touches[0].clientX)
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null) {
      return
    }
    const dx = e.changedTouches[0].clientX - touchX.current
    if (Math.abs(dx) > 40) {
      go(index + (dx < 0 ? 1 : -1))
    }
    touchX.current = null
  }

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    props.onSearch(query.trim())
  }

  const current = items[index]

  return (
    <section
      className={classSet({ "kh-hero": true, "kh-hero--still": reducedMotion })}
      aria-roledescription="carousel"
      aria-label={i18n._({ id: "khcn.hero.region", message: "About the forum" })}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="kh-hero__slides">
        {items.map((slide, i) => (
          <div
            key={slide.key}
            className={classSet({ "kh-hero__slide": true, "kh-hero__slide--active": i === index })}
            role="group"
            aria-roledescription="slide"
            aria-label={i18n._({ id: "khcn.hero.slide", message: "{name} ({n} of {count})", values: { name: slide.name, n: i + 1, count } })}
            aria-hidden={i !== index}
          >
            {photos[slide.key] ? (
              <div className="kh-hero__photo" style={{ backgroundImage: `url("${photos[slide.key]}")` }} />
            ) : (
              <div className="kh-hero__scene">
                <HeroLandscape variant={slide.variant} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="kh-hero__wash" aria-hidden="true" />
      <div className="kh-hero__tech" aria-hidden="true">
        <HeroTech />
      </div>
      <div className="kh-hero__inner container">
        <BronzeDrum className="kh-hero__drum" />
        <div className="kh-hero__content">
          <p className="kh-hero__eyebrow">{i18n._({ id: "khcn.hero.eyebrow", message: "Forum" })}</p>
          <h2 className="kh-hero__title">
            <span className="kh-hero__title-line">{i18n._({ id: "khcn.hero.title1", message: "Science & Technology" })}</span>
            <span className="kh-hero__title-line kh-hero__title-line--gold">{i18n._({ id: "khcn.hero.title2", message: "and Innovation" })}</span>
          </h2>
          {/* Hai vế cùng một hàng (máy tính bảng trở lên), ngăn bằng vạch + hình thoi vàng; mobile xếp dọc */}
          <p className="kh-hero__sub">
            <span className="kh-hero__sub-part">{i18n._({ id: "khcn.hero.sub1", message: "Connecting the community's intelligence" })}</span>
            <span className="kh-hero__sub-sep" aria-hidden="true" />
            <span className="kh-hero__sub-part">{i18n._({ id: "khcn.hero.sub2", message: "Shaping the future of Hà Tĩnh" })}</span>
          </p>

          <form className="kh-hero__search" role="search" onSubmit={submitSearch}>
            <Icon sprite={IconSearch} className="kh-hero__search-icon" />
            <input
              id="kh-hero-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={i18n._({ id: "khcn.hero.search.placeholder", message: "Have an idea or proposal?" })}
              aria-label={i18n._({ id: "khcn.hero.search.button", message: "Search" })}
            />
            <button type="submit" className="kh-hero__search-btn">
              <Icon sprite={IconSearch} />
              <span>{i18n._({ id: "khcn.hero.search.button", message: "Search" })}</span>
            </button>
          </form>

          <div className="kh-hero__chips" role="group">
            <button
              type="button"
              className={classSet({ "kh-hero__chip": true, "kh-hero__chip--active": props.activeCategory === null })}
              onClick={() => props.onSelectCategory(null)}
            >
              {i18n._({ id: "khcn.hero.chip.all", message: "All" })}
            </button>
            {props.categories.map((c) => (
              <button
                type="button"
                key={c.slug}
                title={c.hint}
                className={classSet({ "kh-hero__chip": true, "kh-hero__chip--active": props.activeCategory === c.slug })}
                onClick={() => props.onSelectCategory(c.slug)}
              >
                {c.label}
                {props.countPerCategory[c.slug] ? <span className="kh-hero__chip-count">{props.countPerCategory[c.slug]}</span> : null}
              </button>
            ))}
          </div>
        </div>

        {/* Ấn ký "Hà Tĩnh": thẻ kính góc phải trên, tên chữ serif nghiêng tô gradient trắng → vàng, vạch vàng, hai dòng khẩu hiệu */}
        {callig !== "done" && (
          <div ref={calligRef} className={classSet({ "kh-hero__callig": true, "kh-hero__callig--flying": callig === "flying" })} aria-hidden="true">
            <span className="kh-hero__callig-name">Hà Tĩnh</span>
            <span className="kh-hero__callig-rule" />
            <span className="kh-hero__callig-line kh-hero__callig-line--accent">{i18n._({ id: "khcn.hero.callig.line1", message: "Innovation" })}</span>
            <span className="kh-hero__callig-line">{i18n._({ id: "khcn.hero.callig.line2", message: "Reaching for the future" })}</span>
          </div>
        )}

        <div className="kh-hero__caption" aria-live="polite">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z" />
            <circle cx="12" cy="10" r="2.5" />
          </svg>
          <span key={current.key} className="kh-hero__caption-text">
            {current.name}
          </span>
        </div>

        <div className="kh-hero__controls">
          <button type="button" className="kh-hero__nav" onClick={() => go(index - 1)} aria-label={i18n._({ id: "khcn.hero.prev", message: "Previous" })}>
            <Icon sprite={IconChevronUp} className="kh-hero__nav-icon kh-hero__nav-icon--prev" />
          </button>
          <div className="kh-hero__dots" role="tablist">
            {items.map((slide, i) => (
              <button
                type="button"
                key={slide.key}
                role="tab"
                aria-selected={i === index}
                aria-label={i18n._({ id: "khcn.hero.goto", message: "Go to {name}", values: { name: slide.name } })}
                className={classSet({ "kh-hero__dot": true, "kh-hero__dot--active": i === index })}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
          <button type="button" className="kh-hero__nav" onClick={() => go(index + 1)} aria-label={i18n._({ id: "khcn.hero.next", message: "Next" })}>
            <Icon sprite={IconChevronUp} className="kh-hero__nav-icon kh-hero__nav-icon--next" />
          </button>
        </div>
      </div>
    </section>
  )
}
